import React, { useMemo, useEffect } from 'react';
import { StackedBarChart } from '@/components/ui/chart-wrapper';
import DataTable from './DataTable';
import WidgetBase from './WidgetBase';
import { useWidgetData } from '../../hooks/useWidgetData';
import { exportToCSV } from '../../utils/exportToCSV';
import { useChartColors } from '../../hooks/useChartColors';

/**
 * Distribution Stack Bar Widget Component
 * Modernized replacement for MultiStackBar_Dist_List.js
 * Displays commission distribution with tier breakdown
 * Most complex widget - handles special commission data
 */
function DistStackBarWidget({ data, onLegendUpdate, onExportReady, onDataReady, mode = 'chart' }) {
    const colors = useChartColors();

    // Fetch data using custom hook
    const { data: apiData, loading, error } = useWidgetData(
        data.url,
        data.RequestJson,
        data.RequestJson1
    );

    // Extract legend/summary data
    const summaryData = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;

        try {
            if (apiData[0].legend && apiData[0].legend.length > 0) {
                return {
                    total: apiData[0].legend[0]?.legendValue || 0,
                    totalLabel: apiData[0].legend[0]?.legendText || 'Total',
                    breakdown: apiData[0].legend[1]?.legendValue || 0,
                    breakdownLabel: apiData[0].legend[1]?.legendText || 'Breakdown'
                };
            }
            return null;
        } catch (err) {
            return null;
        }
    }, [apiData]);

    // Transform API data to Chart.js stacked bar format
    // Matches old MultiStackBar_Dist_List.js: uses columnDataSet1[0] (Dist) + columnDataSet2[0] (SubDist)
    const chartData = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;

        try {
            const ds1 = apiData[0].columnDataSet1?.[0];
            const ds2 = apiData[0].columnDataSet2?.[0];

            if (!ds1 || !ds1.labels || !ds1.data) return null;

            const labels = ds1.labels;
            const colorCode = data.ColorCode || colors.chart2;

            const datasets = [
                {
                    label: ds1.toplabel || 'Distributor',
                    data: ds1.data,
                    backgroundColor: colors.chart1,
                    borderColor: colors.chart1,
                    stack: '1',
                    hoverBackgroundColor: colors.chart1,
                    borderWidth: 1
                }
            ];

            if (ds2 && ds2.data) {
                datasets.push({
                    label: ds2.toplabel || 'Sub-Distributor',
                    data: ds2.data,
                    backgroundColor: colorCode,
                    borderColor: colorCode,
                    stack: '1',
                    hoverBackgroundColor: colors.chart2,
                    borderWidth: 1
                });
            }

            return { labels, datasets };
        } catch (err) {
            console.error('Error transforming distribution chart data:', err);
            return null;
        }
    }, [apiData, data, colors]);

    // Table data from listDataSet (same format as other widgets)
    const { tableData, columns } = useMemo(() => {
        if (!apiData || apiData.length === 0) return { tableData: [], columns: [] };

        try {
            const listDataSet = apiData[0].listDataSet;
            if (!listDataSet || listDataSet.length === 0) return { tableData: [], columns: [] };

            // First row defines column headers (header text is in .data field)
            const cols = (listDataSet[0].datasets || []).map((ds, idx) => ({
                key: `col${idx}`,
                label: ds.data || `Column ${idx + 1}`,
                sortable: true,
                format: (val) => val ?? '-'
            }));

            // Data rows start at index 1 (index 0 is the header row)
            const rows = listDataSet.slice(1).map(item => {
                const row = {};
                (item.datasets || []).forEach((ds, idx) => {
                    row[`col${idx}`] = ds.data;
                });
                return row;
            });

            return { tableData: rows, columns: cols };
        } catch (err) {
            console.error('Error building dist-stack table data:', err);
            return { tableData: [], columns: [] };
        }
    }, [apiData]);

    // Notify parent whether this widget has data (used to disable toggle / export buttons).
    // Use derived chartData/tableData — raw apiData can be non-empty with no real content.
    useEffect(() => {
        if (loading) return;
        if (onDataReady) onDataReady(chartData !== null || tableData.length > 0);
    }, [loading, chartData, tableData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Export handler — mirrors old MultiStackBar_Dist_List.js which exported listDataSet rows
    const handleExport = () => {
        if (tableData.length > 0) {
            exportToCSV(tableData, data.title || 'total-revenue-earned', columns);
        }
    };

    // Report legend value to parent (number shown before title in card header)
    // Old MultiStackBar_Dist_List.js used sumOfLegendValues[0].sumOfLegendValue for the header count
    useEffect(() => {
        if (!apiData || apiData.length === 0 || !onLegendUpdate) return;
        try {
            // Try sumOfLegendValues first (distributor-specific), fall back to legend[0]
            const sumVal = apiData[0].sumOfLegendValues?.[0]?.sumOfLegendValue
                ?? apiData[0].legend?.[0]?.legendValue
                ?? '';
            if (sumVal) onLegendUpdate(data.id, String(sumVal));
        } catch (e) { /* ignore */ }
    }, [apiData]);

    // Register export handler with parent (for header-level export button)
    useEffect(() => {
        if (onExportReady) onExportReady(handleExport);
    }, [chartData]);

    const colorCode = data.ColorCode || colors.chart2;

    // Chart options matching old MultiStackBar_Dist_List.js
    const chartOptions = {
        scales: {
            x: {
                offset: true,
                title: {
                    display: !!(data.XlabelString),
                    text: data.XlabelString || ''
                },
                ticks: {
                    display: false,
                    autoSkip: true,
                    maxTicksLimit: 5
                },
                grid: { display: false, drawBorder: false }
            },
            y: {
                title: {
                    display: !!(data.YlabelString),
                    text: data.YlabelString || ''
                },
                beginAtZero: true,
                grid: { drawBorder: false, display: false }
            }
        },
        plugins: {
            legend: { display: false },
            tooltip: { mode: 'index', intersect: false }
        }
    };

    // Chart component — always a real element (mirrors old "data-not-found" span when no data)
    const chartComponent = chartData ? (
        <div>
            {summaryData && (summaryData.totalLabel || summaryData.breakdownLabel) && (
                <div className="flex justify-center gap-6 mb-3">
                    {summaryData.totalLabel && (
                        <div className="flex items-center gap-2">
                            <span className="inline-block w-3 h-3 rounded-sm bg-primary" />
                            <span className="font-semibold text-sm">{summaryData.total}</span>
                            <span className="text-sm text-muted-foreground">{summaryData.totalLabel}</span>
                        </div>
                    )}
                    {summaryData.breakdownLabel && (
                        <div className="flex items-center gap-2">
                            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: colorCode }} />
                            <span className="font-semibold text-sm">{summaryData.breakdown}</span>
                            <span className="text-sm text-muted-foreground">{summaryData.breakdownLabel}</span>
                        </div>
                    )}
                </div>
            )}
            <StackedBarChart
                datasets={chartData.datasets}
                labels={chartData.labels}
                height={280}
                options={chartOptions}
            />
        </div>
    ) : (
        <div className="flex items-center justify-center py-12">
            <p className="text-primary/70">No Data Available</p>
        </div>
    );

    // Table component — always a real element (mirrors old "data-not-found" span when no list data)
    const tableComponent = tableData.length > 0 ? (
        <DataTable
            columns={columns}
            data={tableData}
            pagination={{ enabled: true, itemsPerPage: 6 }}
            sortable={true}
            searchable={false}
        />
    ) : (
        <div className="flex items-center justify-center py-12">
            <p className="text-primary/70">No Data Available</p>
        </div>
    );

    return (
        <WidgetBase
            loading={loading}
            error={error}
            chartComponent={chartComponent}
            tableComponent={tableComponent}
            mode={mode}
        />
    );
}

export default DistStackBarWidget;
